'use client';

import { useCallback, useEffect, useRef, useState, type FC } from 'react';

import type { DeviceType, PromotionWideBannerItemType, PromotionWideBannerWidgetData } from '@weberus/shared-types';
import { ScrollToTopButton } from '@weberus/ui/components/scroll-to-top-button';
import { useIsPcWidth } from '@weberus/ui/hooks/use-is-pc-width';
import { cn, TopNavigation } from '@yanolja-rnd/nol-design-system';
import { IcClose } from '@yanolja-rnd/nol-icons';
import { throttle } from 'lodash-es';
import Modal from 'react-modal';

import { PromotionWideBannerItem } from './promotion-wide-banner-item';

import type { TopNavigationIconButtonType } from '@yanolja-rnd/nol-design-system';

interface PromotionWideBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: PromotionWideBannerWidgetData;
  deviceType: DeviceType | null;
}

export const PromotionWideBannerGrid: FC<{ items: PromotionWideBannerItemType[] }> = ({ items }) => {
  return (
    <div className="grid grid-cols-1 px-16 gap-y-12 pc:grid-cols-2 pc:gap-y-24 pc:px-0 pc:gap-x-16">
      {items.map((item, i) => (
        <PromotionWideBannerItem key={`${i}-${item.title}`} {...item} />
      ))}
    </div>
  );
};

export const PromotionWideBannerModal: FC<PromotionWideBannerModalProps> = ({ isOpen, onClose, data, deviceType }) => {
  const items = data.items[0].items;
  const [isMounted, setIsMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isPcWidth = useIsPcWidth();

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const leftButton: TopNavigationIconButtonType = {
    'aria-label': 'Close',
    icon: <IcClose />,
    onClick: onClose,
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;

    if (!isMounted || !scrollElement) {
      return;
    }

    setIsScrolled(scrollElement.scrollTop > 0);
    const handleScroll = throttle(() => {
      setIsScrolled(scrollElement.scrollTop > 0);
    }, 150);

    scrollElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      setIsMounted(false);
    };
  }, [isMounted]);

  const setScrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    setIsMounted(!!node);
  }, []);

  useEffect(() => {
    !isOpen && setIsMounted(false);
  }, [isOpen]);

  return (
    <Modal
      ariaHideApp={false}
      className={{
        afterOpen: isPcWidth ? '' : 'animate-modalOpen',
        base: 'h-full w-full',
        beforeClose: isPcWidth ? '' : '!animate-modalClose',
      }}
      closeTimeoutMS={210}
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName={{
        afterOpen: isPcWidth ? '' : 'animate-overlayOpen',
        base: 'fixed opacity-0 inset-0 bg-scrim z-high left-1/2 -translate-x-1/2 max-w-mobile-size pc:max-w-[none] w-full pc:opacity-100',
        beforeClose: isPcWidth ? '' : '!animate-overlayClose',
      }}
    >
      <div className="flex flex-col h-full bg-static-white">
        <header
          className={cn('min-h-52 pc:min-h-76 px-6 z-[1]', {
            'shadow-[0px_6px_8px_-6px_rgba(0,0,0,0.2)]': isScrolled,
          })}
        >
          <div className="mx-auto max-w-pc-size">
            <TopNavigation deviceType={deviceType ?? 'etc'} leftButton={leftButton} title="전체보기" />
          </div>
        </header>

        <div className="flex-auto w-full overflow-auto" ref={setScrollRef}>
          <div className="mx-auto mb-80 pc:mt-10 pc:w-pc-size">
            <PromotionWideBannerGrid items={items} />
            <ScrollToTopButton
              className="bottom-[1.6rem] right-[unset] flex w-full justify-end px-16"
              targetRef={scrollRef}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
